import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type Base = 'hex' | 'oct' | 'bin' | 'dec';

interface State {
    bytes: Uint8Array | null;
    fileName: string;
    width: number;
    base: Base;
    upper: boolean;
    showAscii: boolean;
    error: string | null;
    copied: boolean;
    inputMode: 'file' | 'text' | 'hex';
    textInput: string;
    hexInput: string;
}

export default class HexDumpViewerComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        bytes: null,
        fileName: '',
        width: 16,
        base: 'hex',
        upper: false,
        showAscii: true,
        error: null,
        copied: false,
        inputMode: 'text',
        textInput: 'Hello, world!\nxconvert',
        hexInput: '48 65 6c 6c 6f',
    };

    componentDidMount() {
        this.parseTextInput();
    }

    handleFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            const bytes = new Uint8Array(reader.result as ArrayBuffer);
            this.setState({ bytes, fileName: file.name, error: null, inputMode: 'file' });
        };
        reader.onerror = () => this.setState({ error: 'Failed to read file.' });
        reader.readAsArrayBuffer(file);
    };

    onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) this.handleFile(f);
    };

    parseTextInput = () => {
        const enc = new TextEncoder();
        this.setState({ bytes: enc.encode(this.state.textInput), fileName: '(text input)', error: null });
    };

    parseHexInput = () => {
        try {
            const cleaned = this.state.hexInput.replace(/[^0-9a-fA-F]/g, '');
            if (cleaned.length % 2 !== 0) {
                this.setState({ error: 'Hex input must have an even number of nibbles.' });
                return;
            }
            const arr = new Uint8Array(cleaned.length / 2);
            for (let i = 0; i < cleaned.length; i += 2) {
                arr[i / 2] = parseInt(cleaned.substring(i, i + 2), 16);
            }
            this.setState({ bytes: arr, fileName: '(hex input)', error: null });
        } catch {
            this.setState({ error: 'Invalid hex input.' });
        }
    };

    private formatByte(b: number): string {
        switch (this.state.base) {
            case 'hex': {
                const s = b.toString(16).padStart(2, '0');
                return this.state.upper ? s.toUpperCase() : s;
            }
            case 'oct': return b.toString(8).padStart(3, '0');
            case 'bin': return b.toString(2).padStart(8, '0');
            case 'dec': return b.toString(10).padStart(3, ' ');
        }
    }

    private get dump(): string {
        const { bytes, width, base } = this.state;
        if (!bytes || bytes.length === 0) return '';
        const lines: string[] = [];
        const offsetWidth = Math.max(8, bytes.length.toString(16).length);
        for (let off = 0; off < bytes.length; off += width) {
            const chunk = bytes.slice(off, off + width);
            const offStr = off.toString(16).padStart(offsetWidth, '0');
            const hex = Array.from(chunk).map(b => this.formatByte(b)).join(' ');
            const padLength = width - chunk.length;
            const padding = padLength > 0 ? '   '.repeat(padLength) : '';
            const ascii = this.state.showAscii
                ? '  ' + Array.from(chunk).map(b => (b >= 32 && b < 127) ? String.fromCharCode(b) : '.').join('')
                : '';
            lines.push(`${offStr}: ${hex}${padding}${ascii}`);
        }
        const _ = base; // silence unused
        return lines.join('\n');
    }

    copyOutput = () => {
        if (!this.dump) return;
        navigator.clipboard.writeText(this.dump).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const { bytes, fileName, width, base, upper, showAscii, error, copied, inputMode, textInput, hexInput } = this.state;
        const dump = this.dump;
        const lineCount = dump ? dump.split('\n').length : 0;

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Input</span></div>
                    <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.25rem' }}>
                        {(['text', 'hex', 'file'] as const).map(m => (
                            <button key={m}
                                className={`${genStyles.iconBtn} ${inputMode === m ? genStyles.iconBtnPrimary : ''}`}
                                onClick={() => this.setState({ inputMode: m })}>
                                {m === 'text' ? 'Text' : m === 'hex' ? 'Hex string' : 'File'}
                            </button>
                        ))}
                    </div>
                    {inputMode === 'text' && (
                        <div className={genStyles.field}>
                            <label htmlFor="hd-text">Text to dump</label>
                            <textarea id="hd-text" rows={6} value={textInput}
                                onChange={e => this.setState({ textInput: e.target.value })} />
                            <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`}
                                onClick={this.parseTextInput} style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}>
                                Parse text
                            </button>
                        </div>
                    )}
                    {inputMode === 'hex' && (
                        <div className={genStyles.field}>
                            <label htmlFor="hd-hex">Hex string</label>
                            <textarea id="hd-hex" rows={6} value={hexInput}
                                placeholder="48 65 6c 6c 6f or 48656c6c6f" spellCheck={false}
                                onChange={e => this.setState({ hexInput: e.target.value })} />
                            <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`}
                                onClick={this.parseHexInput} style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}>
                                Parse hex
                            </button>
                        </div>
                    )}
                    {inputMode === 'file' && (
                        <div>
                            <label className={genStyles.iconBtn}>
                                📂 Choose file
                                <input type="file" onChange={this.onFileChange} style={{ display: 'none' }} />
                            </label>
                            {fileName && <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>{fileName} ({bytes?.length ?? 0} bytes)</div>}
                        </div>
                    )}
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Format</span></div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="hd-width">Bytes per row</label>
                            <input id="hd-width" type="number" inputMode="decimal" min={4} max={64} step={4} value={width}
                                onChange={e => this.setState({ width: Math.min(64, Math.max(4, +e.target.value || 16)) })} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="hd-base">Base</label>
                            <select id="hd-base" value={base} onChange={e => this.setState({ base: e.target.value as Base })}>
                                <option value="hex">Hex (00–FF)</option>
                                <option value="oct">Octal (000–377)</option>
                                <option value="bin">Binary</option>
                                <option value="dec">Decimal</option>
                            </select>
                        </div>
                    </div>
                    {base === 'hex' && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                            <input type="checkbox" checked={upper} onChange={e => this.setState({ upper: e.target.checked })} />
                            Uppercase hex
                        </label>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={showAscii} onChange={e => this.setState({ showAscii: e.target.checked })} />
                        Show ASCII column
                    </label>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                expandable
                form={form}
                previewLabel="Hex dump"
                previewMeta={bytes ? `${bytes.length} bytes · ${lineCount} lines` : undefined}
                preview={<pre className={genStyles.previewArea} aria-label="Hex dump"
                    style={{ whiteSpace: 'pre', overflowX: 'auto', wordBreak: 'normal' }}>{dump || '(no input)'}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!dump}>
                        {copied ? '✓ Copied' : '📋 Copy'}
                    </button>
                }
                error={error}
            />
        );
    }
}
